import { Switch, Route, Redirect, Router as WouterRouter } from "wouter";
import { lazy, Suspense, type ComponentType } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { publicPath } from "@/lib/public-url";
import {
  ACGME_MINIMUMS_ROUTE_SLUGS,
  AUDIENCE_ROUTE_SLUGS,
  COMING_SOON_SPECIALTY_ROUTE_SLUGS,
  COMPARE_ROUTE_SLUGS,
  LIVE_SPECIALTY_ROUTE_SLUGS,
} from "@/client-route-slugs";

// Routes per plans/08-website.md §3. SpotMeter wrapped the app in
// QueryClientProvider + TooltipProvider + Toaster (React Query / shadcn
// tooltip / sonner). This site has no queries, no tooltips, and no toasts, so
// per §3 ("fine to drop the provider if no page uses it") those providers and
// their dependencies are dropped to keep the bundle tiny. Speed is the brand.
// THE APP-UI GALLERY, dev server only: every drawn app element on one page, so
// a recreation can be checked against the app before a page uses it. Behind
// `import.meta.env.DEV`, so the production bundle and the prerender never see
// the route or the chunk (src/routes.ts does not list it).
const AppUiGallery = import.meta.env.DEV
  ? lazy(() => import("@/pages/app-ui-gallery"))
  : null;

export type AppComponents = {
  Home: ComponentType;
  Features: ComponentType;
  Demo: ComponentType;
  Insights: ComponentType;
  StudyLab: ComponentType;
  Programs: ComponentType;
  Evidence: ComponentType;
  BestCaseLogApp: ComponentType;
  AccuracyTest: ComponentType;
  Ambassadors: ComponentType;
  About: ComponentType;
  Pricing: ComponentType;
  Reimbursement: ComponentType;
  Privacy: ComponentType;
  Security: ComponentType;
  Terms: ComponentType;
  Download: ComponentType;
  Support: ComponentType;
  Feedback: ComponentType;
  Changelog: ComponentType;
  Press: ComponentType;
  Sucks: ComponentType;
  HelpIndex: ComponentType;
  HelpArticlePage: ComponentType;
  BlogIndex: ComponentType;
  BlogPostPage: ComponentType;
  AdvertorialPage: ComponentType;
  Join: ComponentType<{ slug: string }>;
  SpecialtyPage: ComponentType<{ slug: string }>;
  ComingSoonSpecialtyPage: ComponentType<{ slug: string }>;
  AudiencePage: ComponentType<{ slug: string }>;
  ComparePage: ComponentType<{ slug: string }>;
  AcgmeMinimumsIndex: ComponentType;
  AcgmeMinimumsPage: ComponentType<{ slug: string }>;
  NotFound: ComponentType;
};

function Router({ components }: { components: AppComponents }) {
  const {
    Home,
    Features,
    Demo,
    Insights,
    StudyLab,
    Programs,
    Evidence,
    BestCaseLogApp,
    AccuracyTest,
    Ambassadors,
    About,
    Pricing,
    Reimbursement,
    Privacy,
    Security,
    Terms,
    Download,
    Support,
    Feedback,
    Changelog,
    Press,
    Sucks,
    HelpIndex,
    HelpArticlePage,
    BlogIndex,
    BlogPostPage,
    AdvertorialPage,
    Join,
    SpecialtyPage,
    ComingSoonSpecialtyPage,
    AudiencePage,
    ComparePage,
    AcgmeMinimumsIndex,
    AcgmeMinimumsPage,
    NotFound,
  } = components;

  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/features" component={Features} />
      <Route path="/demo" component={Demo} />
      <Route path="/insights" component={Insights} />
      <Route path="/study-lab" component={StudyLab} />
      <Route path="/programs" component={Programs} />
      {/* The white paper. Directly under /programs for the reason
          src/routes.ts gives: it is the argument that page makes the offer
          for. */}
      <Route path="/case-log-gap" component={Evidence} />
      {/* The dated ranked guide, beside the white paper for the reason
          src/routes.ts gives. */}
      <Route path="/best-case-log-app-2027" component={BestCaseLogApp} />
      {/* The Case Log Accuracy Test (D-179). Mounted even while
          ACCURACY_TEST_LIVE is false: the route is then absent from
          src/routes.ts, so no document is prerendered and 404.html
          client-renders this path for the owner's review, noindexed. */}
      <Route path="/case-log-accuracy-test" component={AccuracyTest} />
      <Route path="/ambassadors" component={Ambassadors} />
      {/* Who is behind the app, what it commits to, and what it will never
          do. Above the legal routes for the reason src/routes.ts gives. */}
      <Route path="/about" component={About} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/reimbursement" component={Reimbursement} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/security" component={Security} />
      <Route path="/terms" component={Terms} />
      <Route path="/download" component={Download} />
      {/* RETIRED 2026-10-23 (LUX-36): /extension, the extension's explainer,
          and /chrome, its install sheet. The extension is being phased out in
          favour of filing from the iPhone app, and the site no longer recruits
          for it. Neither path is in src/routes.ts, so nothing is prerendered
          for it and the static host answers its 404.html (noindex). That file
          client-renders this <Switch>, and these two rows replace the URL with
          the extension's help guide, which keeps every extension article. Same
          move as pages/help/article.tsx's unknown-slug redirect; a Replit
          rewrite cannot express a 301. */}
      <Route path="/extension">
        <Redirect to={publicPath("/help/acgme-submit")} replace />
      </Route>
      <Route path="/chrome">
        <Redirect to={publicPath("/help/acgme-submit")} replace />
      </Route>
      <Route path="/help" component={HelpIndex} />
      <Route path="/help/:slug" component={HelpArticlePage} />
      {/* The blog (E-364). `/blog/:slug` is a param route like /help/:slug,
          and the 404 for an unknown slug is the page's own: pages/blog/post.tsx
          renders NotFound for any slug that is not a PUBLISHED post, and
          pages/blog/index.tsx renders NotFound while nothing is published.
          Only published paths are in src/routes.ts, so only they are
          prerendered; every other /blog URL is the host's 404.html. */}
      <Route path="/blog" component={BlogIndex} />
      <Route path="/blog/:slug" component={BlogPostPage} />
      <Route path="/support" component={Support} />
      {/* The public ideas board (plan 086). A static route like every other
          page here: the ideas themselves live in the API and arrive on the
          client, but the URL set stays fixed at build time. */}
      <Route path="/feedback" component={Feedback} />
      {/* Release notes, rendered from the app's own "What's new" — the site and
          the app read the same synced list, so they cannot disagree. */}
      <Route path="/changelog" component={Changelog} />
      <Route path="/press" component={Press} />
      {/* THE SHARE PAGE. A static route like every other page here. Nothing
          on this site links to it: the door is a social caption, which is
          also why it carries its own data module and stylesheet rather than
          joining an existing registry. */}
      <Route path="/sucks" component={Sucks} />
      {/* The advertorials (D-229): article-shaped landing pages a paid ad
          links to. A param route like /blog/:slug, with the same gate:
          pages/advertorial.tsx renders NotFound for any slug that is not in
          LIVE_ADVERTORIAL_ROUTE_SLUGS (src/client-route-slugs.ts), and only
          live paths are in src/routes.ts, so only they are prerendered. */}
      <Route path="/read/:slug" component={AdvertorialPage} />
      {/* THE ONE `/:param` ROUTE ON THIS SITE, and the exception the blocks
          below are careful to explain they are not. An invite slug is minted per
          program in the database after this bundle is built, so there is no list
          to turn into static routes. src/routes.ts's SHELL_ROUTES declares the
          single prerendered shell that answers every /join/* URL; the
          404-for-an-unknown-slug guarantee is kept at RUNTIME by pages/join.tsx,
          which asks the API and renders "this invite link isn't active" on a
          404. Do NOT copy this shape for a page whose slugs are known at build
          time — those stay static, for the reasons spelled out below. */}
      <Route path="/join/:slug">
        {(params) => <Join slug={params.slug ?? ""} />}
      </Route>
      {/* One STATIC route per live specialty — never a `/:slug` param route, so
          an unknown slug falls through to NotFound instead of rendering an empty
          specialty page with a 200.

          The array goes into <Switch> BARE. wouter's Switch flattens a child
          array to find the matching <Route>, but treats a <>…</> fragment as a
          single opaque child it cannot look inside — wrapping these would
          silently kill every specialty route while leaving the build green. */}
      {LIVE_SPECIALTY_ROUTE_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`}>
          <SpecialtyPage slug={slug} />
        </Route>
      ))}
      {/* One STATIC route per coming-soon specialty — same rules as above: no
          `/:slug` param route, and the array goes into <Switch> BARE. */}
      {COMING_SOON_SPECIALTY_ROUTE_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`}>
          <ComingSoonSpecialtyPage slug={slug} />
        </Route>
      ))}
      {/* One STATIC route per audience row (src/data/audiences.ts), for exactly
          the reasons the specialty block above spells out: never a `/:slug` param
          route, and the array goes into <Switch> BARE — a fragment here would be
          one opaque child wouter cannot look inside, silently killing every
          audience route while the build stayed green. */}
      {AUDIENCE_ROUTE_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`}>
          <AudiencePage slug={slug} />
        </Route>
      ))}
      {/* One STATIC route per comparison row (src/data/compare.ts). Same rules
          again: never a `/compare/:slug` param route, so an unknown comparison
          slug falls through to NotFound instead of rendering an empty page with
          a 200 — and the array goes into <Switch> BARE, because a fragment is
          one opaque child wouter cannot look inside. */}
      {COMPARE_ROUTE_SLUGS.map((slug) => (
        <Route key={slug} path={`/compare/${slug}`}>
          <ComparePage slug={slug} />
        </Route>
      ))}
      {/* The ACGME case-minimums library (E-149): the index, then one STATIC
          route per specialty. Same rules as every registry block above: never
          an `/acgme-case-minimums/:slug` param route, so an unknown slug falls
          through to NotFound, and the array goes into <Switch> BARE. */}
      <Route path="/acgme-case-minimums" component={AcgmeMinimumsIndex} />
      {ACGME_MINIMUMS_ROUTE_SLUGS.map((slug) => (
        <Route key={slug} path={`/acgme-case-minimums/${slug}`}>
          <AcgmeMinimumsPage slug={slug} />
        </Route>
      ))}
      {AppUiGallery ? (
        <Route path="/__app-ui">
          <Suspense fallback={null}>
            <AppUiGallery />
          </Suspense>
        </Route>
      ) : null}
      <Route component={NotFound} />
    </Switch>
  );
}

// `ssrPath` is passed only by entry-server.tsx during the prerender pass so
// wouter resolves the right route server-side. On the client it's undefined and
// wouter uses the browser location.
export function App({
  ssrPath,
  components,
}: {
  ssrPath?: string;
  components: AppComponents;
}) {
  return (
    <WouterRouter
      base={import.meta.env.BASE_URL.replace(/\/$/, "")}
      ssrPath={ssrPath}
    >
      <ScrollToTop />
      <Router components={components} />
    </WouterRouter>
  );
}
